// TEST-ONLY tripwire for the "qualifying event keeps disappearing" bug.
// Records every delete / build / vanish of a qualifyingEvents node — with a
// timestamp and a JS stack trace — into /qe_audit on the TEST database, so the
// exact caller that clears an event can be read back via REST (no browser
// needed). Hard-gated to the test site: on the club DB every function no-ops.
import { push, ref, set, onValue } from 'firebase/database'

import { db } from './firebase.js'
import { IS_TEST_SITE } from '../config/siteMode.js'

// Write one audit record. Best-effort — never throws into the caller.
export function qeAudit(action, extra = {}) {
  if (!IS_TEST_SITE) return
  try {
    const rec = {
      ts: Date.now(),
      action,
      ...extra,
      url: typeof window !== 'undefined' ? window.location.href : '',
      stack: new Error(`qeAudit:${action}`).stack || '',
    }
    // fire-and-forget; a failed audit write must not break the real flow
    set(push(ref(db, 'qe_audit')), rec).catch(() => {})
    // eslint-disable-next-line no-console
    console.warn(`[qe_audit] ${action}`, rec)
  } catch { /* ignore */ }
}

// Watch the whole qualifyingEvents node and record when any event id goes from
// present → absent (the "vanish"), or when a present event's team count drops
// to zero (the "roster with no scores" symptom). Returns an unsubscribe fn.
export function installQualifyingVanishMonitor() {
  if (!IS_TEST_SITE) return () => {}
  let prev = null // { [eventId]: teamCount }
  const off = onValue(ref(db, 'qualifyingEvents'), (snap) => {
    const val = snap.val() || {}
    const now = {}
    for (const [id, ev] of Object.entries(val)) {
      now[id] = ev && ev.teams ? Object.keys(ev.teams).length : 0
    }
    if (prev) {
      for (const id of Object.keys(prev)) {
        if (!(id in now)) {
          qeAudit('EVENT_VANISHED', { eventId: id, prevTeams: prev[id] })
        } else if (prev[id] > 0 && now[id] === 0) {
          qeAudit('TEAMS_EMPTIED', { eventId: id, prevTeams: prev[id] })
        }
      }
    }
    prev = now
  }, (e) => qeAudit('MONITOR_LISTENER_ERROR', { message: String(e?.message || e) }))
  return off
}
